import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  type LucideIcon,
  MoreVertical,
  Plus,
  RefreshCw,
  Upload,
} from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu.tsx';
import { TabsContent } from '@/components/ui/Tabs.tsx';
import TabScreen from '@/components/ui/TabScreen.tsx';
import RawTransactionsTable from '@/features/dashboard/rawBankTransactions/components/RawTransactionsTable.tsx';
import FinancialTransactionsTable from '@/features/dashboard/financialTransactions/components/FinancialTransactionsTable.tsx';
import PlannedTransactionsTable from '@/features/dashboard/plannedTransactions/components/PlannedTransactionsTable.tsx';
import BankImportModal from '@/features/dashboard/rawBankTransactions/components/BankImportModal.tsx';
import CreateFinancialTransactionModal from '@/features/dashboard/financialTransactions/components/CreateFinancialTransactionModal.tsx';
import useRecalculatePaymentStatuses from '@/features/dashboard/financialTransactions/hooks/useRecalculatePaymentStatuses.ts';
import GlobalExpensesTable from '@/features/dashboard/expenses/components/GlobalExpensesTable.tsx';
import CreateExpenseModal from '@/features/dashboard/expenses/components/CreateExpenseModal.tsx';
import {
  FINANCES_TABS,
  financesRoute,
  FinancesTab,
} from '@/features/dashboard/finances/routes/financesRoute.ts';

type FinancesActionId = 'addTransaction' | 'planExpense' | 'importBank';

const ACTION_ORDER: FinancesActionId[] = [
  'addTransaction',
  'planExpense',
  'importBank',
];

const FinancesPage = () => {
  const { tab } = financesRoute.useSearch();
  const navigate = useNavigate();

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateExpenseModalOpen, setIsCreateExpenseModalOpen] =
    useState(false);
  const { mutate: recalculate, isPending: isRecalculating } =
    useRecalculatePaymentStatuses();

  const actions: Record<
    FinancesActionId,
    { label: string; icon: LucideIcon; run: () => void }
  > = {
    addTransaction: {
      label: 'Dodaj transakcję',
      icon: Plus,
      run: () => setIsCreateModalOpen(true),
    },
    planExpense: {
      label: 'Zaplanuj wydatek',
      icon: Plus,
      run: () => setIsCreateExpenseModalOpen(true),
    },
    importBank: {
      label: 'Importuj z banku',
      icon: Upload,
      run: () => setIsImportModalOpen(true),
    },
  };

  /*
   * Akcja widoczna obok kebaba — po jednej na zakładkę; reszta zostaje w menu,
   * żeby nagłówek nie łamał się na telefonie. Mapa siedzi w komponencie, bo
   * `FinancesTab` przychodzi przez plik trasy, który importuje tę stronę.
   */
  const primaryByTab: Partial<Record<FinancesTab, FinancesActionId>> = {
    [FinancesTab.TRANSACTIONS]: 'addTransaction',
    [FinancesTab.EXPENSES]: 'planExpense',
    [FinancesTab.IMPORTED]: 'importBank',
  };

  // Brak zakładki w adresie to pierwsza z listy albo menu mobilne — w obu
  // przypadkach ekran nie ma jeszcze swojej akcji głównej.
  const primaryId = tab ? primaryByTab[tab] : undefined;
  const primary = primaryId ? actions[primaryId] : undefined;

  return (
    <>
      <TabScreen
        tabs={FINANCES_TABS}
        value={tab}
        onChange={(value) =>
          navigate({ to: '/app/finances', search: { tab: value } })
        }
        title="Finanse"
        menuDescription="Wpłaty, wydatki i wyciągi bankowe. Wybierz sekcję."
        primaryAction={
          primary && {
            label: primary.label,
            icon: primary.icon,
            onClick: primary.run,
          }
        }
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ActionButton
                tone="outline"
                size="sm"
                iconOnly
                aria-label="Więcej akcji"
              >
                <MoreVertical />
              </ActionButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {ACTION_ORDER.map((id) => {
                const { label, icon: Icon, run } = actions[id];
                return (
                  <DropdownMenuItem key={id} onClick={run}>
                    <Icon size={16} />
                    {label}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuItem
                onClick={() => recalculate()}
                disabled={isRecalculating}
              >
                <RefreshCw
                  size={16}
                  className={isRecalculating ? 'animate-spin' : ''}
                />
                Przelicz finanse
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      >
        <TabsContent value={FinancesTab.PLANNED}>
          <PlannedTransactionsTable />
        </TabsContent>
        <TabsContent value={FinancesTab.EXPENSES}>
          <GlobalExpensesTable />
        </TabsContent>
        <TabsContent value={FinancesTab.TRANSACTIONS}>
          <FinancialTransactionsTable />
        </TabsContent>
        <TabsContent value={FinancesTab.IMPORTED}>
          <RawTransactionsTable />
        </TabsContent>
      </TabScreen>

      {isImportModalOpen && (
        <BankImportModal onClose={() => setIsImportModalOpen(false)} />
      )}

      {isCreateModalOpen && (
        <CreateFinancialTransactionModal
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}

      {isCreateExpenseModalOpen && (
        <CreateExpenseModal
          onClose={() => setIsCreateExpenseModalOpen(false)}
        />
      )}
    </>
  );
};

export default FinancesPage;
